import { useState } from "react";
import {
  getActivitySettings,
  saveActivitySettingsAPI,
} from "@/mocks/adminData";
import type { ActivitySettings, ActivityItem } from "@/mocks/adminData";
import { useImageUpload } from "@/hooks/useImageUpload";

export default function ActivityTab({ token }: { token: string }) {
  const [settings, setSettings] = useState<ActivitySettings>(getActivitySettings());
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");
  const [editingItem, setEditingItem] = useState<ActivityItem | null>(null);
  const [editTitle, setEditTitle] = useState("");
  const [editTitleUrl, setEditTitleUrl] = useState("");
  const [editLines, setEditLines] = useState("");
  const [rightImageInput, setRightImageInput] = useState("");

  const { trigger: triggerRightImg, el: rightImgInput } = useImageUpload(
    (url) => { setSettings(prev => ({ ...prev, right_image: url })); },
    { uploadPath: "admin/settings/upload-image", token, fieldName: "image" }
  );

  const handleSave = async () => {
    setLoading(true);
    const success = await saveActivitySettingsAPI(settings, token);
    setLoading(false);
    setMsg(success ? "保存成功，前台已同步更新" : "已保存到本地缓存（服务器连接失败）");
    setTimeout(() => setMsg(""), 3000);
  };

  const openEditItem = (item: ActivityItem) => {
    setEditingItem(item);
    setEditTitle(item.title);
    setEditTitleUrl(item.title_url || "");
    setEditLines(item.lines.join("\n"));
  };

  const saveItem = () => {
    if (!editingItem) return;
    const lines = editLines.split("\n").map(l => l.trim()).filter(l => l.length > 0);
    setSettings(prev => ({
      ...prev,
      items: prev.items.map(it =>
        it.id === editingItem.id ? { ...it, title: editTitle, title_url: editTitleUrl.trim(), lines } : it
      ),
    }));
    setEditingItem(null);
  };

  const addItem = () => {
    const newId = "act_" + Date.now();
    const newItem: ActivityItem = { id: newId, title: "【新活动】标题", title_url: "", lines: ["活动内容第一行", "活动内容第二行"] };
    setSettings(prev => ({ ...prev, items: [...prev.items, newItem] }));
    openEditItem(newItem);
  };

  const deleteItem = (id: string) => {
    if (!window.confirm("确认删除该活动？")) return;
    setSettings(prev => ({ ...prev, items: prev.items.filter(it => it.id !== id) }));
  };

  const moveItem = (id: string, dir: "up" | "down") => {
    const idx = settings.items.findIndex(it => it.id === id);
    if (idx < 0) return;
    const newItems = [...settings.items];
    if (dir === "up" && idx > 0) {
      [newItems[idx - 1], newItems[idx]] = [newItems[idx], newItems[idx - 1]];
    } else if (dir === "down" && idx < newItems.length - 1) {
      [newItems[idx], newItems[idx + 1]] = [newItems[idx + 1], newItems[idx]];
    }
    setSettings(prev => ({ ...prev, items: newItems }));
  };

  return (
    <div>
      {rightImgInput}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base font-bold text-gray-800">活动专栏编辑</h2>
        <button onClick={addItem} className="flex items-center gap-1 bg-red-500 text-white text-xs px-3 py-2 rounded-lg cursor-pointer hover:bg-red-600 transition-colors whitespace-nowrap">
          <i className="ri-add-line"></i>添加活动
        </button>
      </div>

      {msg && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-xs text-green-600 flex items-center gap-2">
          <i className="ri-checkbox-circle-line"></i>{msg}
        </div>
      )}

      {/* 右侧图片设置 */}
      <div className="bg-white border border-gray-100 rounded-xl p-4 mb-4">
        <h3 className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
          <i className="ri-image-line text-red-400"></i>活动右侧图片
        </h3>
        <div className="flex items-start gap-3 mb-3">
          <div className="w-20 h-20 rounded-lg bg-gray-100 overflow-hidden flex-shrink-0 border border-gray-200 flex items-center justify-center">
            {settings.right_image ? (
              <img src={settings.right_image} alt="右侧图片" className="w-full h-full object-cover"
                onError={e => { (e.target as HTMLImageElement).style.display = "none"; }} />
            ) : (
              <i className="ri-image-line text-gray-300 text-2xl"></i>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex gap-2 mb-2">
              <button type="button" onClick={triggerRightImg}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-lg text-xs text-gray-600 cursor-pointer transition-colors whitespace-nowrap">
                <i className="ri-upload-2-line"></i>上传图片
              </button>
              {settings.right_image && (
                <button type="button" onClick={() => setSettings(prev => ({ ...prev, right_image: "" }))}
                  className="flex items-center gap-1 px-3 py-1.5 text-xs text-red-400 hover:text-red-600 cursor-pointer whitespace-nowrap">
                  <i className="ri-delete-bin-line"></i>清除
                </button>
              )}
            </div>
            <div className="flex gap-2">
              <input
                value={rightImageInput}
                onChange={e => setRightImageInput(e.target.value)}
                onKeyDown={e => {
                  if (e.key === "Enter" && rightImageInput.trim()) {
                    setSettings(prev => ({ ...prev, right_image: rightImageInput.trim() }));
                    setRightImageInput("");
                  }
                }}
                className="flex-1 border border-gray-200 rounded-lg px-3 py-1.5 text-xs outline-none focus:border-red-400 min-w-0"
                placeholder="或粘贴图片链接（https://...）"
              />
              <button
                onClick={() => {
                  if (rightImageInput.trim()) {
                    setSettings(prev => ({ ...prev, right_image: rightImageInput.trim() }));
                    setRightImageInput("");
                  }
                }}
                className="px-3 py-1.5 bg-red-500 text-white rounded-lg text-xs cursor-pointer hover:bg-red-600 transition-colors whitespace-nowrap flex-shrink-0"
              >
                <i className="ri-check-line"></i>
              </button>
            </div>
          </div>
        </div>
        <p className="text-xs text-gray-400">活动卡片右侧展示的人物/装饰图片</p>
      </div>

      {/* 活动跳转链接 */}
      <div className="bg-white border border-gray-100 rounded-xl p-4 mb-4">
        <h3 className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
          <i className="ri-links-line text-red-400"></i>活动跳转链接
        </h3>
        <div>
          <label className="text-xs font-semibold text-gray-600 mb-1 block">点击活动区域跳转域名</label>
          <input
            value={settings.redirect_url || ""}
            onChange={e => setSettings(prev => ({ ...prev, redirect_url: e.target.value }))}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-red-400"
            placeholder="如：https://663.com（留空则不跳转）"
          />
          <div className="mt-2 flex items-start gap-1.5">
            <i className="ri-information-line text-gray-400 text-xs mt-0.5 flex-shrink-0"></i>
            <p className="text-xs text-gray-500 leading-relaxed">
              设置后，用户点击整个活动专栏区域时将自动在新标签页打开该链接；留空则不跳转。
            </p>
          </div>
          {settings.redirect_url && (
            <div className="mt-2 flex items-center gap-2 bg-green-50 border border-green-100 rounded-lg px-3 py-2">
              <i className="ri-checkbox-circle-line text-green-500 text-xs flex-shrink-0"></i>
              <span className="text-xs text-green-600 truncate">已设置跳转：{settings.redirect_url}</span>
              <button
                type="button"
                onClick={() => setSettings(prev => ({ ...prev, redirect_url: "" }))}
                className="ml-auto text-xs text-red-400 hover:text-red-600 cursor-pointer whitespace-nowrap flex-shrink-0"
              >
                清除
              </button>
            </div>
          )}
        </div>
      </div>

      {/* 商务联系方式 */}
      <div className="bg-white border border-gray-100 rounded-xl p-4 mb-4">
        <h3 className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
          <i className="ri-telegram-line text-red-400"></i>商务联系方式
        </h3>
        <div className="space-y-4">
          <div>
            <label className="text-xs font-semibold text-gray-600 mb-1 block">TG账号（显示在活动底部）</label>
            <input value={settings.contact} onChange={e => setSettings(prev => ({ ...prev, contact: e.target.value }))}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-red-400"
              placeholder="如：@hx_zs888" />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-600 mb-1 block">TG链接</label>
            <input value={settings.contact_url} onChange={e => setSettings(prev => ({ ...prev, contact_url: e.target.value }))}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-red-400"
              placeholder="如：https://t.me/hx_zs888" />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-600 mb-1 block">底部说明文字</label>
            <textarea value={settings.footer_note} onChange={e => setSettings(prev => ({ ...prev, footer_note: e.target.value }))}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-red-400 resize-none"
              rows={2} maxLength={500} />
          </div>
        </div>
      </div>

      {/* 活动列表 */}
      <div className="space-y-2 mb-4">
        {settings.items.map((item, idx) => (
          <div key={item.id} className="bg-white border border-gray-100 rounded-xl p-3">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-800 mb-1">{item.title}</p>
                <p className="text-xs font-medium text-gray-500 line-clamp-2">{item.lines.slice(0, 2).join(" / ")}</p>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <button onClick={() => moveItem(item.id, "up")} disabled={idx === 0}
                  className="w-6 h-6 flex items-center justify-center rounded hover:bg-gray-100 cursor-pointer disabled:opacity-30">
                  <i className="ri-arrow-up-s-line text-gray-500 text-sm"></i>
                </button>
                <button onClick={() => moveItem(item.id, "down")} disabled={idx === settings.items.length - 1}
                  className="w-6 h-6 flex items-center justify-center rounded hover:bg-gray-100 cursor-pointer disabled:opacity-30">
                  <i className="ri-arrow-down-s-line text-gray-500 text-sm"></i>
                </button>
                <button onClick={() => openEditItem(item)}
                  className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer">
                  <i className="ri-edit-line text-gray-500 text-sm"></i>
                </button>
                <button onClick={() => deleteItem(item.id)}
                  className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-red-50 cursor-pointer">
                  <i className="ri-delete-bin-line text-red-400 text-sm"></i>
                </button>
              </div>
            </div>
          </div>
        ))}
        {settings.items.length === 0 && (
          <div className="text-center py-8 text-gray-400 text-sm">暂无活动，点击上方「添加活动」</div>
        )}
      </div>

      <button onClick={handleSave} disabled={loading}
        className="w-full mt-2 py-3 bg-gradient-to-r from-red-500 to-orange-400 text-white rounded-xl text-sm font-semibold cursor-pointer hover:opacity-90 transition-opacity disabled:opacity-50 whitespace-nowrap">
        {loading ? (
          <span className="flex items-center justify-center gap-2">
            <i className="ri-loader-4-line animate-spin"></i>保存中...
          </span>
        ) : (
          <span><i className="ri-save-line mr-1.5"></i>保存活动设置</span>
        )}
      </button>

      {/* 编辑活动弹窗 */}
      {editingItem && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-end md:items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-5 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-gray-800">编辑活动</h3>
              <button onClick={() => setEditingItem(null)} className="w-7 h-7 flex items-center justify-center cursor-pointer">
                <i className="ri-close-line text-gray-500"></i>
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-semibold text-gray-600 mb-1 block">活动标题</label>
                <input value={editTitle} onChange={e => setEditTitle(e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-red-400"
                  placeholder="如：【活动一】每月发量:" />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600 mb-1 block">
                  标题跳转链接
                  <span className="ml-1.5 font-normal text-gray-400">（可选，点击标题跳转）</span>
                </label>
                <input value={editTitleUrl} onChange={e => setEditTitleUrl(e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-red-400"
                  placeholder="如：https://663.com（留空则不跳转）" />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600 mb-1 block">活动内容（每行一条）</label>
                <textarea value={editLines} onChange={e => setEditLines(e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-red-400 resize-none font-mono"
                  rows={8} placeholder={"每行输入一条内容\n如：提供> 500万量的机房,奖励:1888$"} maxLength={2000} />
                <div className="mt-2 p-2.5 bg-amber-50 border border-amber-100 rounded-lg space-y-1.5">
                  <p className="text-xs font-semibold text-amber-700 flex items-center gap-1">
                    <i className="ri-palette-line"></i>文字样式设置方法
                  </p>
                  <p className="text-xs text-amber-600 font-semibold">【红色文字】</p>
                  <p className="text-xs text-amber-600">· 数字和金额（如 1888$、500万）自动变红</p>
                  <p className="text-xs text-amber-600">· 用 <code className="bg-amber-100 px-1 rounded font-mono">**文字**</code> 包裹变红</p>
                  <div className="border-t border-amber-200 pt-1.5 mt-0.5">
                    <p className="text-xs text-amber-600 font-semibold">【超链接跳转】</p>
                    <p className="text-xs text-amber-600">· 格式：<code className="bg-amber-100 px-1 rounded font-mono">[显示文字](链接地址)</code></p>
                    <p className="text-xs font-mono text-amber-700 bg-amber-100 px-2 py-1 rounded">联系[663客服](https://663.com)登记</p>
                    <p className="text-xs text-amber-500">→ 显示：联系<span className="text-red-500 font-semibold underline">663客服</span>登记（点击跳转）</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex gap-3 mt-5">
              <button onClick={() => setEditingItem(null)} className="flex-1 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-600 cursor-pointer hover:bg-gray-50 whitespace-nowrap">取消</button>
              <button onClick={saveItem} className="flex-1 py-2.5 bg-gradient-to-r from-red-500 to-orange-400 text-white rounded-xl text-sm font-semibold cursor-pointer hover:opacity-90 whitespace-nowrap">确认</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
